/**
 * Byte ranges of a PDF for pdf.js, in the chunks it asks for, with the reads
 * it is about to make started ahead of it.
 *
 * pdf.js will not hand over page one until it has found the last page, and
 * in a flat page tree — every page a direct kid of the root, as Word and most
 * generators write them — it finds it by fetching every page's dictionary,
 * one after the next. Across a large file those sit in as many chunks, and
 * one round trip each is most of the wait. The primer reads the tree itself
 * and asks for all of those chunks at once; pdf.js then finds them in hand.
 */

import type { RangeSource } from '@/helpers/rangeSource'

export const PDF_CHUNK_BYTES = 64 * 1024
/**
 * The most chunks one request carries. pdf.js fetching a whole file behind
 * the first page asks for it in one run; split, a page the reader jumps to
 * waits for one of these rather than for all of it.
 */
const MAX_RUN_CHUNKS = 16
/** Past this many chunks the priming costs more bytes than the walk costs time. */
const MAX_PRIMED_CHUNKS = 96
/** A flat tree this small is walked in a handful of reads anyway. */
const MIN_PRIMED_KIDS = 8
/** An object's dictionary, as far as a primer needs to read into it. */
const OBJECT_WINDOW = 16 * 1024

export interface PdfChunkSource {
  size: number
  read: (
    begin: number,
    end: number,
    options?: { release?: boolean },
  ) => Promise<Uint8Array>
  /** Starts reading these chunks, in as few requests as runs of them allow. */
  prefetch: (chunkIndexes: Iterable<number>) => void
  /**
   * Reads the rest of the file, a few runs at a time, so a reader of it
   * walking chunk by chunk finds each one in hand. Settles when every chunk
   * has been read, or at the first failure.
   */
  fill: (signal?: AbortSignal) => Promise<void>
  /**
   * Keeps released chunks until `until` settles, for another reader still
   * working through the same bytes (the page-tree primer beside pdf.js).
   */
  holdReleases: (until: Promise<unknown>) => void
}

/** Runs a fill keeps in flight: enough to use the link, not all of it. */
const FILL_CONCURRENCY = 2

export const pdfChunkSource = (
  source: RangeSource,
  chunkSize = PDF_CHUNK_BYTES,
): PdfChunkSource => {
  const chunks = new Map<number, Promise<Uint8Array>>()
  /** Handed over for good; read again only if asked for again. */
  const released = new Set<number>()
  let holds = 0
  const lastChunk = Math.ceil(source.size / chunkSize) - 1

  const fetchRun = (first: number, last: number) => {
    const run = source.read(
      first * chunkSize,
      Math.min(source.size, (last + 1) * chunkSize),
    )
    for (let index = first; index <= last; index += 1) {
      const offset = (index - first) * chunkSize
      const chunk = run.then((bytes) =>
        bytes.subarray(offset, offset + chunkSize),
      )
      chunk.catch(() => undefined)
      chunks.set(index, chunk)
    }
  }

  const prefetch = (chunkIndexes: Iterable<number>) => {
    const missing = [...new Set(chunkIndexes)]
      .filter(
        (index) =>
          index >= 0 &&
          index <= lastChunk &&
          !chunks.has(index) &&
          !released.has(index),
      )
      .sort((a, b) => a - b)
    let runStart = -1
    for (let i = 0; i < missing.length; i += 1) {
      if (runStart < 0) runStart = missing[i]
      if (
        missing[i + 1] !== missing[i] + 1 ||
        missing[i] - runStart + 1 >= MAX_RUN_CHUNKS
      ) {
        fetchRun(runStart, missing[i])
        runStart = -1
      }
    }
  }

  /**
   * `release` hands the bytes over for good: pdf.js keeps every chunk it is
   * given, so holding them here as well would keep the file twice.
   */
  const read = async (begin: number, end: number, { release = false } = {}) => {
    const first = Math.floor(begin / chunkSize)
    const last = Math.floor((Math.min(end, source.size) - 1) / chunkSize)
    const indexes = Array.from(
      { length: last - first + 1 },
      (_, i) => first + i,
    )
    for (const index of indexes) {
      if (!chunks.has(index)) released.delete(index)
    }
    prefetch(indexes)
    const pending = indexes.map((index) => chunks.get(index)!)
    if (release) {
      for (const index of indexes) {
        released.add(index)
        if (holds === 0) chunks.delete(index)
      }
    }
    const parts = await Promise.all(pending)
    const out = new Uint8Array(Math.min(end, source.size) - begin)
    let at = 0
    for (let i = 0; i < parts.length; i += 1) {
      const chunkStart = (first + i) * chunkSize
      const from = Math.max(0, begin - chunkStart)
      const to = Math.min(parts[i].byteLength, end - chunkStart)
      out.set(parts[i].subarray(from, to), at)
      at += to - from
    }
    return out
  }

  const fill = async (signal?: AbortSignal) => {
    let nextWindow = 0
    const walk = async () => {
      while (nextWindow <= lastChunk && !signal?.aborted) {
        const first = nextWindow
        nextWindow += MAX_RUN_CHUNKS
        const window = Array.from(
          { length: Math.min(MAX_RUN_CHUNKS, lastChunk - first + 1) },
          (_, i) => first + i,
        )
        prefetch(window)
        await Promise.all(window.map((index) => chunks.get(index)))
      }
    }
    await Promise.all(Array.from({ length: FILL_CONCURRENCY }, walk))
  }

  const holdReleases = (until: Promise<unknown>) => {
    holds += 1
    const settle = () => {
      holds -= 1
      if (holds > 0) return
      for (const index of released) chunks.delete(index)
    }
    until.then(settle, settle)
  }

  return { size: source.size, read, prefetch, fill, holdReleases }
}

const latin1 = new TextDecoder('latin1')

/** Object offsets from a classic cross-reference table; null for a cross-reference stream. */
const readXrefTable = async (source: PdfChunkSource) => {
  const tail = latin1.decode(
    await source.read(Math.max(0, source.size - 1024), source.size),
  )
  const startxref = [...tail.matchAll(/startxref\s+(\d+)/g)].at(-1)?.[1]
  if (!startxref) return null
  const at = Number(startxref)
  const head = latin1.decode(
    await source.read(at, Math.min(source.size, at + 64)),
  )
  if (!head.startsWith('xref')) return null

  // Twenty bytes an entry; read the table and its trailer whole.
  const table = latin1.decode(await source.read(at, source.size))
  const offsets = new Map<number, number>()
  const subsection = /\s*(\d+)\s+(\d+)[ \t]*(?:\r\n|\r|\n)/y
  let cursor = 4
  for (;;) {
    subsection.lastIndex = cursor
    const match = subsection.exec(table)
    // The trailer, where the numbers stop.
    if (!match) break
    const [first, count] = [Number(match[1]), Number(match[2])]
    cursor = subsection.lastIndex
    for (let i = 0; i < count; i += 1) {
      const entry = table.slice(cursor + i * 20, cursor + i * 20 + 18)
      if (entry[17] === 'n') offsets.set(first + i, Number(entry.slice(0, 10)))
    }
    cursor += count * 20
  }
  const root = /\/Root\s+(\d+)\s+\d+\s+R/.exec(table.slice(cursor))?.[1]
  return root ? { offsets, root: Number(root) } : null
}

const readObject = async (source: PdfChunkSource, offset: number) =>
  latin1.decode(
    await source.read(offset, Math.min(source.size, offset + OBJECT_WINDOW)),
  )

/**
 * Starts fetching every chunk holding a page dictionary of a flat page tree.
 * Does nothing for a tree pdf.js can skip through by its counts, for a file
 * indexed by a cross-reference stream, or for one it cannot read; pdf.js
 * reads those as it would have.
 */
export const primePageTree = async (source: PdfChunkSource) => {
  const xref = await readXrefTable(source)
  if (!xref) return
  const catalogOffset = xref.offsets.get(xref.root)
  if (catalogOffset === undefined) return
  const pagesRef = /\/Pages\s+(\d+)\s+\d+\s+R/.exec(
    await readObject(source, catalogOffset),
  )?.[1]
  const pagesOffset = pagesRef && xref.offsets.get(Number(pagesRef))
  if (!pagesOffset) return
  const pages = await readObject(source, pagesOffset)
  const kidsList = /\/Kids\s*\[([^\]]*)\]/.exec(pages)?.[1]
  const count = Number(/\/Count\s+(\d+)/.exec(pages)?.[1] ?? NaN)
  if (!kidsList) return
  const kids = [...kidsList.matchAll(/(\d+)\s+\d+\s+R/g)].map((match) =>
    Number(match[1]),
  )
  if (kids.length !== count || kids.length < MIN_PRIMED_KIDS) return

  const chunkIndexes = new Set<number>()
  for (const kid of kids) {
    const offset = xref.offsets.get(kid)
    if (offset !== undefined)
      chunkIndexes.add(Math.floor(offset / PDF_CHUNK_BYTES))
  }
  if (chunkIndexes.size > MAX_PRIMED_CHUNKS) return
  source.prefetch(chunkIndexes)
}
